import { useState, useEffect } from 'react'
import { supabase, HOUSEHOLD_COLUMNS, type Household, type Member } from '../lib/supabase'
import {
  isLastMember, isHouseholdFull, isPayerLocked, isRateLimited,
  LAST_MEMBER_MESSAGE, HOUSEHOLD_FULL_MESSAGE, PAYER_LOCKED_MESSAGE, RATE_LIMITED_MESSAGE,
} from '../lib/rpcError'
import { useIsVisible } from './useIsVisible'

interface HouseholdState {
  household: Household | null
  member: Member | null
  loading: boolean
  error: string | null
}

/** The words for a refused join, whichever call style produced the failure. */
function joinError(body: { code?: unknown; message?: unknown } | null | undefined, status?: number, fallback = 'Invalid invite code'): string {
  if (isRateLimited(body, status)) return RATE_LIMITED_MESSAGE
  if (isHouseholdFull(body, status)) return HOUSEHOLD_FULL_MESSAGE
  if (isLastMember(body, status)) return LAST_MEMBER_MESSAGE
  return fallback
}

// Each account belongs to exactly one household (enforced by a unique constraint
// on members.auth_user_id). The list is chosen once at onboarding — create or
// join by code — so there is no switcher and no active-selection persistence.
//
// A household holds up to six people (031_household_plan.sql); the database
// refuses a join past that with PT402, which every join path here turns into
// HOUSEHOLD_FULL_MESSAGE. The member list is loaded alongside so the counter can say
// so before the join fails.
export function useHousehold(userId: string | null) {
  const [state, setState] = useState<HouseholdState>({
    household: null, member: null, loading: true, error: null,
  })
  const [loadedMembers, setLoadedMembers] = useState<Member[]>([])
  const isVisible = useIsVisible()

  // Signed out there is nothing to fetch, so this is derived rather than written
  // back from the effect below. Writing it there costs an extra render pass and
  // is what react-hooks/set-state-in-effect objects to; the effects here own the
  // *results* of fetches, never the "there was nothing to fetch" case.
  const loading = userId !== null && state.loading

  // The embed names its foreign key: since 025 added households.payer_member_id
  // → members(id) there are two relationships between these tables, and an
  // unqualified `households(...)` embed is ambiguous ("more than one
  // relationship was found").
  useEffect(() => {
    if (!userId) return

    supabase
      .from('members')
      .select(`*, households!members_household_id_fkey(${HOUSEHOLD_COLUMNS})`)
      .eq('auth_user_id', userId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) {
          setState({ household: null, member: null, loading: false, error: error.message })
          return
        }
        if (!data) {
          setState({ household: null, member: null, loading: false, error: null })
          return
        }
        const { households: household, ...member } = data as Member & { households: Household }
        setState({ household, member, loading: false, error: null })
      })
  }, [userId])

  // Everyone on the list, kept live: joins, removals and renames by any member
  // land here (022 added members to the realtime publication, with REPLICA
  // IDENTITY FULL so a DELETE still names its household). Seeing our own row
  // deleted means we were removed — back to onboarding, the way a fresh load
  // would land there anyway.
  const householdId = state.household?.id
  const ownMemberId = state.member?.id
  useEffect(() => {
    if (!householdId) return
    let cancelled = false
    supabase
      .from('members')
      .select('*')
      .eq('household_id', householdId)
      .order('created_at')
      .then(({ data }) => { if (!cancelled && data) setLoadedMembers(data as Member[]) })
    return () => { cancelled = true }
  }, [householdId])

  // Nobody is on a list we are not on.
  const members = householdId ? loadedMembers : []

  useEffect(() => {
    if (!householdId || !isVisible) return
    const channel = supabase
      .channel(`members-${householdId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'members', filter: `household_id=eq.${householdId}` },
        (payload) => {
          if (payload.eventType === 'DELETE') {
            const gone = (payload.old as Partial<Member>).id
            if (gone && gone === ownMemberId) {
              setState({ household: null, member: null, loading: false, error: null })
              return
            }
            setLoadedMembers(ms => ms.filter(m => m.id !== gone))
            return
          }
          const row = payload.new as Member
          if (row.household_id !== householdId) {
            // Moved to another list — it left us.
            setLoadedMembers(ms => ms.filter(m => m.id !== row.id))
            return
          }
          setLoadedMembers(ms => ms.some(m => m.id === row.id)
            ? ms.map(m => (m.id === row.id ? row : m))
            : [...ms, row])
        })
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [householdId, ownMemberId, isVisible])

  // The household row itself, kept live: a payment (trial → active), a lapse
  // (→ past_due/canceled) or a rename by another member lands here, so the
  // subscribe sheet closes itself after Checkout and every open tab agrees on
  // whether the list can be edited. households is in the realtime publication;
  // RLS limits the stream to our own row. Unreadable columns (the Stripe ids)
  // are dropped by keeping only what we already hold.
  useEffect(() => {
    if (!householdId || !isVisible) return
    const channel = supabase
      .channel(`household-${householdId}`)
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'households', filter: `id=eq.${householdId}` },
        (payload) => {
          const row = payload.new as Partial<Household>
          setState(s => {
            if (!s.household || s.household.id !== row.id) return s
            const next = { ...s.household }
            for (const key of Object.keys(next) as (keyof Household)[]) {
              if (key in row) (next as Record<string, unknown>)[key] = row[key]
            }
            return { ...s, household: next }
          })
        })
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [householdId, isVisible])

  // After a membership change, re-read our row + household the same way the
  // initial load does, so state is exactly what a fresh open would show.
  async function reload() {
    if (!userId) return
    const { data, error } = await supabase
      .from('members')
      .select(`*, households!members_household_id_fkey(${HOUSEHOLD_COLUMNS})`)
      .eq('auth_user_id', userId)
      .maybeSingle()
    if (error) { setState({ household: null, member: null, loading: false, error: error.message }); return }
    if (!data) { setState({ household: null, member: null, loading: false, error: null }); return }
    const { households: household, ...member } = data as Member & { households: Household }
    setState({ household, member, loading: false, error: null })
  }

  // Create a household, its first membership and its six categories in one
  // server-side call (026_membership_rpcs.sql, 049_category_lifecycle.sql): the
  // invite code is generated in the database, so the client never writes it.
  async function createHousehold(name: string, displayName: string) {
    setState(s => ({ ...s, loading: true, error: null }))
    const { data: hid, error } = await supabase.rpc('create_household', {
      p_name: name, p_display_name: displayName,
    })
    if (error || !hid) {
      setState(s => ({ ...s, loading: false, error: error?.message ?? 'Could not create the list' }))
      return
    }
    await reload()
  }

  // Join by invite code — the code is resolved and throttled inside the RPC
  // (household_id_for_code), and the seat guard's PT402 becomes HOUSEHOLD_FULL.
  async function joinHousehold(inviteCode: string, displayName: string) {
    setState(s => ({ ...s, loading: true, error: null }))
    const { error } = await supabase.rpc('join_household', {
      p_code: inviteCode, p_display_name: displayName,
    })
    if (error) { setState(s => ({ ...s, loading: false, error: joinError(error) })); return }
    await reload()
  }

  // Leave the current list and join another by invite code. One account = one
  // household, so this MOVES the single membership (server-side, 026) rather
  // than adding a second — the old list is left behind.
  async function switchHousehold(inviteCode: string): Promise<string | null> {
    const { error } = await supabase.rpc('switch_household', { p_code: inviteCode })
    if (error) return joinError(error)
    await reload()
    return null
  }

  async function renameHousehold(name: string) {
    const trimmed = name.trim()
    const id = state.household?.id
    if (!trimmed || !id) return
    const { error } = await supabase.from('households').update({ name: trimmed }).eq('id', id)
    if (!error) {
      setState(s => ({
        ...s,
        household: s.household ? { ...s.household, name: trimmed } : s.household,
      }))
    }
  }

  // Any member may remove any member of the list, themselves included — except
  // the payer while the subscription is active (026 raises PT423), and the last
  // member of a paying household (022's PT409). Removing ourselves lands on
  // onboarding.
  async function removeMember(memberId: string): Promise<string | null> {
    const { error } = await supabase.rpc('remove_member', { p_member_id: memberId })
    if (error) {
      if (isPayerLocked(error)) return PAYER_LOCKED_MESSAGE
      if (isLastMember(error)) return LAST_MEMBER_MESSAGE
      return error.message
    }
    if (memberId === state.member?.id) {
      setState({ household: null, member: null, loading: false, error: null })
    } else {
      setLoadedMembers(ms => ms.filter(m => m.id !== memberId))
    }
    return null
  }

  // A removed member still knows the code; a fresh one is what makes removing
  // them mean anything. Generated server-side (026), like at creation.
  async function resetInviteCode(): Promise<string | null> {
    const { data, error } = await supabase.rpc('rotate_invite_code')
    if (error) return error.message
    const invite_code = data as string
    setState(s => ({ ...s, household: s.household ? { ...s.household, invite_code } : s.household }))
    return null
  }

  return {
    household: state.household,
    member: state.member,
    members,
    loading,
    error: state.error,
    createHousehold,
    joinHousehold,
    switchHousehold,
    renameHousehold,
    removeMember,
    resetInviteCode,
  }
}
